import * as React from 'react';

import CalendarDay from 'components/molecules/CalendarDay';
import TerrificBridge from 'react-terrific-bridge';

import { compileDynamicRoute } from 'helper/string';
import { delayed } from 'lib/Job';

import { routes } from 'defs';
import { createRef } from 'react';

export type DayType = {
	date: string;
	dayNumber: number | null;
	hasActiveProgram: boolean;
	inFuture: boolean;
	isLastDayOfProgram: boolean;
	isToday: boolean;
	itemsCheckable: boolean;
	key: number;
	openItems: number;
	totalItems: number;
	weekday: string;
	weekdayShort: string;
};

export type PreparedCalendarDayPropsType = {
	modifier?: string;
	abbr: string;
	end?: boolean;
	day?: string;
	date?: string;
	progressState?: string;
	completed?: boolean;
	isPast?: boolean;
	counter?: {
		modifier: string;
		count: number;
	};
};

export type CalendarProps = {
	selectedDayNumber: number | null;
	days: Array<DayType>;
	navigate: (route: string) => {};
	routeKey: string;
	signature: string; // used for updating process
};

export class Calendar extends React.Component<CalendarProps> {
	private componentRef: React.RefObject<HTMLInputElement>;
	constructor(props: CalendarProps) {
		super(props);
		this.componentRef = createRef();
	}

	public registerComponent() {
		TerrificBridge.registerComponent(this.componentRef.current);
	}

	public componentDidMount() {
		if (this.props.days.length !== 0) {
			this.registerComponent();
		}
	}

	public componentDidUpdate(prevProps: CalendarProps): void {
		if (this.props.routeKey !== prevProps.routeKey) {
			this.registerComponent();
			return;
		}
		if (prevProps.days.length === 0 && this.props.days.length !== 0) {
			this.registerComponent();
		}
	}

	public componentWillUnmount(): void {
		if (this.props.days.length > 0) {
			TerrificBridge.unregisterComponent(this.componentRef.current);
		}
	}

	public shouldComponentUpdate(nextProps: CalendarProps) {
		if (this.props.routeKey !== nextProps.routeKey) {
			return true;
		}

		if (this.props.days.length < 1 || nextProps.days.length < 1) {
			return true;
		}

		if (this.props.signature === nextProps.signature && this.props.routeKey === nextProps.routeKey) {
			return false;
		}

		return this.props.signature !== nextProps.signature;
	}

	public handleDayClick = (dayNumber: number): void => {
		if (dayNumber) {
			delayed(() => {
				this.props.navigate(
					compileDynamicRoute(routes.today.day.href, {
						day: dayNumber,
					})
				);
			}, 500);
		}
	};

	public getDayProps(day: DayType): PreparedCalendarDayPropsType {
		let dayProps: Partial<PreparedCalendarDayPropsType> = {
			abbr: day.weekdayShort.toUpperCase(),
		};

		if (day.dayNumber === null) {
			dayProps = {
				...dayProps,
				modifier: 'disabled',
			};
		} else if (day.isLastDayOfProgram) {
			dayProps = {
				...dayProps,
				modifier: 'future',
				end: true,
				day: day.weekday.toUpperCase(),
				date: day.date,
			};

			if (day.openItems === 0 && day.totalItems !== 0) {
				dayProps = {
					modifier: 'today',
					progressState: 'completed',
					completed: true,
					end: true,
					abbr: day.weekdayShort.toUpperCase(),
					day: day.weekday.toUpperCase(),
					date: day.date,
				};
			}

			if (day.openItems !== 0) {
				dayProps = {
					...dayProps,
					progressState: 'counter',
					counter: {
						modifier: 'small',
						count: day.openItems,
					},
				};
			}
		} else if (day.inFuture) {
			dayProps = {
				...dayProps,
				modifier: 'future',
				day: day.weekday.toUpperCase(),
				date: day.date,
			};
		} else if (day.isToday) {
			if (day.openItems === 0 && day.totalItems !== 0) {
				dayProps = {
					...dayProps,
					modifier: 'today',
					progressState: 'completed',
					completed: true,
					day: day.weekday.toUpperCase(),
					date: day.date,
				};
			} else if (day.openItems === 0 && day.totalItems === 0) {
				dayProps = {
					...dayProps,
					modifier: 'today',
					progressState: 'completed',
					completed: true,
					day: day.weekday.toUpperCase(),
					date: day.date,
				};
			} else if (day.openItems !== 0) {
				dayProps = {
					...dayProps,
					modifier: 'today',
					progressState: 'counter',
					counter: {
						modifier: 'small',
						count: day.openItems,
					},
					day: day.weekday.toUpperCase(),
					date: day.date,
				};
			}
		} else {
			if (day.openItems === 0 && day.totalItems !== 0) {
				dayProps = {
					...dayProps,
					progressState: 'completed',
					completed: true,
					day: day.weekday.toUpperCase(),
					date: day.date,
					isPast: true,
				};
			} else if (day.openItems === day.totalItems) {
				dayProps = {
					...dayProps,
					day: day.weekday.toUpperCase(),
					date: day.date,
					counter: {
						modifier: 'small',
						count: day.openItems,
					},
					isPast: true,
				};
			} else if (day.itemsCheckable) {
				dayProps = {
					...dayProps,
					progressState: 'counter',
					counter: {
						modifier: 'small',
						count: day.openItems,
					},
					day: day.weekday.toUpperCase(),
					date: day.date,
					isPast: true,
				};
			} else {
				dayProps = {
					...dayProps,
					progressState: 'todo-past',
					day: day.weekday.toUpperCase(),
					date: day.date,
					counter: {
						modifier: 'small',
						count: day.openItems,
					},
					isPast: true,
				};
			}
		}

		return dayProps as PreparedCalendarDayPropsType;
	}

	public render() {
		const { days } = this.props;

		if (!(days instanceof Array) || days.length < 1) {
			return null;
		}

		return (
			<div className="m-calendar" data-t-name="Calendar" ref={this.componentRef}>
				<div className="m-calendar__days js-m-calendar__days">
					{days.map((day: DayType) => {
						const dayProps: any = {
							...this.getDayProps(day),
							dayNumber: day.dayNumber,
							itemsCheckable: day.itemsCheckable,
							hasAlreadyStartedChecking: Boolean(day.openItems > 0 && day.openItems < day.totalItems),
						};

						if (day.dayNumber === this.props.selectedDayNumber) {
							dayProps.state = 'selected';
						}

						return (
							<div
								key={`$.day-${day.date}`}
								className={`m-calendar__day ${
									dayProps.modifier === 'disabled' ? '' : 'js-m-calendar__day'
								}`}
							>
								<CalendarDay {...dayProps} handleClick={this.handleDayClick} />
							</div>
						);
					})}
				</div>
			</div>
		);
	}
}

export default Calendar;
